/**
 * User Detail Utils
 * 
 * Utility functions for formatting and calculating user detail data.
 */

import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import type { User } from "@/services/authService";

/**
 * Format a date string to Indonesian locale format
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

/**
 * Format a date string to date-time format with Indonesian locale
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  try {
    return format(new Date(dateString), "dd MMM yyyy HH:mm", { locale: idLocale });
  } catch {
    return "-";
  }
}

/**
 * Same predicate as `UserPDFGeneratorService/generators/KTA.ts` (`isMasaLaku2100`).
 * Empty string treated as false (DB normally always has a value here).
 */
export function isMasaLaku2100Sentinel(masa_laku: string | null | undefined): boolean {
  if (masa_laku == null || masa_laku === "") return false;
  return (
    new Date(masa_laku).getFullYear() === 2100 ||
    String(masa_laku).includes("2100-01-01")
  );
}

/**
 * Tanggal masa berlaku seperti pada generator PDF KTA (`KTA.ts`: `masaLakuDate` lalu `formatDate`).
 */
export function formatMasaLakuLikeKtaPdf(
  masa_laku: string | null | undefined,
  masa_laku_kta: string | null | undefined
): string {
  if (masa_laku == null || masa_laku === "") return "-";
  if (isMasaLaku2100Sentinel(masa_laku) && masa_laku_kta) {
    const d = new Date(masa_laku_kta);
    if (!Number.isNaN(d.getTime())) return formatDate(masa_laku_kta);
  }
  return formatDate(masa_laku);
}

/**
 * Tipe IAR IFR2100 (normalisasi: huruf kecil, tanpa spasi/tanda hubung).
 * Untuk tipe ini masa berlaku KTA diambil dari `masa_laku_kta`, bukan dari tanggal 2100 di IAR.
 */
export function isIfr2100TipeIar(tipe_iar: string | null | undefined): boolean {
  const n = tipe_iar?.trim().toLowerCase().replace(/[\s-]+/g, "");
  return n === "ifr2100";
}

/**
 * Tampilkan Masa Laku KTA di kartu IAR bila tipe IFR2100 atau masa_laku memakai sentinel 2100.
 */
export function shouldShowMasaLakuKtaInIarSection(
  tipe_iar: string | null | undefined,
  masa_laku: string | null | undefined
): boolean {
  return isIfr2100TipeIar(tipe_iar) || isMasaLaku2100Sentinel(masa_laku);
}

/**
 * Calculate profile completion percentage
 */
export function calculateProfileCompletion(user: User): number {
  let completed = 0;
  let total = 0;
  
  const fields = [
    user.name,
    user.email,
    user.callsign,
    user.jenis_kelamin,
    user.alamat,
    user.provinsi,
    user.kabupaten,
    user.no_hp,
    user.iar,
    user.isPasswordSet,
  ];
  
  fields.forEach((field) => {
    total++;
    if (field !== undefined && field !== null && field !== "") {
      completed++;
    }
  });
  
  return Math.round((completed / total) * 100);
}

/**
 * Get user initials from name or callsign
 */
export function getUserInitials(user: User): string {
  if (user.name) {
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return user.callsign?.slice(0, 2).toUpperCase() || "U";
}

